/* ============================================================================
 * Movatic WMS — Frontend | CesarFilho 932048016
 * Arquivo: src/pages/workforce/ShiftTemplatesPage.tsx
 * ----------------------------------------------------------------------------
 * Gestão de Modelos de turno (ShiftTemplate).
 *
 * - Lista modelos vindos do backend (/api/workforce/shift-templates/).
 * - Formulário simples para criar/editar (code, name, horários, pausa, flags).
 * - Usa fetch() com token do authStore (quando existir).
 * - Mantém layout simples, só com componentes já existentes (Button, Input,
 *   Table...), para evitar erros de imports (switch/card/badge).
 * ========================================================================== */

import { useEffect, useState, FormEvent } from "react";
import { useAuthStore } from "@/store/authStore";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Table,
    TableHeader,
    TableHead,
    TableRow,
    TableBody,
    TableCell,
} from "@/components/ui/table";

/** Estrutura esperada do modelo vindo da API. */
type ShiftTemplate = {
    id: number;
    code: string;
    name: string;
    start_time?: string | null; // "HH:MM[:SS]"
    end_time?: string | null; // "HH:MM[:SS]"
    break_minutes?: number | null;
    is_night_shift?: boolean;
    is_active?: boolean;
};

/** Estado do formulário (valores dos inputs). */
type FormState = {
    code: string;
    name: string;
    start: string; // "HH:MM"
    end: string; // "HH:MM"
    breakMinutes: string;
    isNight: boolean;
    isActive: boolean;
};

// ---------------------------------------------------------------------------
// Helpers locais
// ---------------------------------------------------------------------------

/** Base da API (respeita VITE_API_BASE_URL; default = "/api"). */
const API_BASE =
    (import.meta as any).env.VITE_API_BASE_URL?.replace(/\/$/, "") || "/api";
const WF_BASE = `${API_BASE}/workforce`;

/** Lê o accessToken do authStore (se existir) e monta headers de auth. */
function buildAuthHeaders(contentTypeJson = true): HeadersInit {
    const state: any = (useAuthStore as any)?.getState?.() ?? {};
    const token: string | undefined = state.accessToken;
    const headers: HeadersInit = {};
    if (contentTypeJson) {
        headers["Content-Type"] = "application/json";
    }
    if (token) {
        headers["Authorization"] = `Bearer ${token}`;
    }
    return headers;
}

/** Normaliza string de hora do backend ("HH:MM[:SS]") para "HH:MM". */
function toTimeField(value?: string | null): string {
    if (!value) return "";
    return value.substring(0, 5);
}

/** Converte valor do input time ("HH:MM") para "HH:MM:SS" para o backend. */
function fromTimeField(value: string): string | null {
    const v = value.trim();
    if (!v) return null;
    if (v.length > 5) return v;
    return `${v}:00`;
}

/** Converte qualquer valor numérico para string segura de input. */
function numberToInput(value: number | null | undefined): string {
    if (value === null || value === undefined) return "";
    return String(value);
}

/** Extrai array de resultados da resposta DRF (com ou sem paginação). */
function extractResults<T = any>(data: any): T[] {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.results)) return data.results;
    return [];
}

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------

export default function ShiftTemplatesPage() {
    const [items, setItems] = useState<ShiftTemplate[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // id do registo que está a ser editado (null = criação)
    const [editingId, setEditingId] = useState<number | null>(null);

    const [form, setForm] = useState<FormState>({
        code: "",
        name: "",
        start: "",
        end: "",
        breakMinutes: "",
        isNight: false,
        isActive: true,
    });

    // -----------------------------------------------------------------------
    // Carregar lista
    // -----------------------------------------------------------------------
    async function loadItems() {
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(`${WF_BASE}/shift-templates/`, {
                method: "GET",
                headers: buildAuthHeaders(false),
                credentials: "include",
            });
            if (!res.ok) {
                throw new Error(`Erro ao carregar modelos (${res.status})`);
            }
            const data = await res.json();
            const list = extractResults<ShiftTemplate>(data);
            list.sort((a, b) => a.code.localeCompare(b.code));
            setItems(list);
        } catch (e: any) {
            console.error(e);
            setError(e.message ?? "Erro ao carregar modelos.");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        void loadItems();
    }, []);

    // -----------------------------------------------------------------------
    // Handlers de formulário
    // -----------------------------------------------------------------------
    function handleChange(field: keyof FormState, value: string | boolean): void {
        setForm((prev) => ({
            ...prev,
            [field]: value,
        }));
    }

    function resetForm() {
        setEditingId(null);
        setForm({
            code: "",
            name: "",
            start: "",
            end: "",
            breakMinutes: "",
            isNight: false,
            isActive: true,
        });
    }

    function handleEdit(item: ShiftTemplate) {
        setEditingId(item.id);
        setForm({
            code: item.code ?? "",
            name: item.name ?? "",
            start: toTimeField(item.start_time),
            end: toTimeField(item.end_time),
            breakMinutes: numberToInput(item.break_minutes),
            isNight: !!item.is_night_shift,
            isActive: item.is_active !== false,
        });
        window.scrollTo({ top: 0, behavior: "smooth" });
    }

    async function handleDelete(id: number) {
        if (!confirm("Confirma eliminar este modelo de turno?")) return;
        setSaving(true);
        setError(null);
        try {
            const res = await fetch(`${WF_BASE}/shift-templates/${id}/`, {
                method: "DELETE",
                headers: buildAuthHeaders(false),
                credentials: "include",
            });
            if (!res.ok && res.status !== 204) {
                throw new Error(`Erro ao eliminar (${res.status})`);
            }
            setItems((prev) => prev.filter((it) => it.id !== id));
            if (editingId === id) resetForm();
        } catch (e: any) {
            console.error(e);
            setError(e.message ?? "Erro ao eliminar modelo.");
        } finally {
            setSaving(false);
        }
    }

    async function handleSubmit(ev: FormEvent) {
        ev.preventDefault();
        setSaving(true);
        setError(null);
        try {
            const payload: any = {
                code: form.code.trim(),
                name: form.name.trim(),
                start_time: fromTimeField(form.start),
                end_time: fromTimeField(form.end),
                break_minutes: form.breakMinutes
                    ? Number(form.breakMinutes.replace(",", "."))
                    : null,
                is_night_shift: form.isNight,
                is_active: form.isActive,
            };

            const isEdit = editingId != null;
            const url = isEdit
                ? `${WF_BASE}/shift-templates/${editingId}/`
                : `${WF_BASE}/shift-templates/`;
            const method = isEdit ? "PATCH" : "POST";

            const res = await fetch(url, {
                method,
                headers: buildAuthHeaders(true),
                credentials: "include",
                body: JSON.stringify(payload),
            });

            if (!res.ok) {
                const text = await res.text();
                console.error("Erro API:", res.status, text);
                throw new Error(
                    `Erro ao ${isEdit ? "guardar" : "criar"} modelo (${res.status})`
                );
            }

            await loadItems();
            resetForm();
        } catch (e: any) {
            console.error(e);
            setError(e.message ?? "Erro ao guardar modelo.");
        } finally {
            setSaving(false);
        }
    }

    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------
    return (
        <div className="p-4 lg:p-6 space-y-6">
            <header className="space-y-1">
                <h1 className="text-2xl font-semibold">
                    Workforce • Modelos de turno
                </h1>
                <p className="text-sm text-slate-300">
                    Mantenha aqui os modelos padrão de turno (MANHÃ/TARDE/NOITE), com
                    horários, pausas e indicação de turno noturno. Estes modelos serão
                    usados no planeamento de turnos e na verificação de disponibilidade.
                </p>
            </header>

            {error && (
                <div className="rounded-md border border-red-500/60 bg-red-500/10 px-4 py-2 text-sm text-red-100">
                    {error}
                </div>
            )}

            <div className="grid gap-6 lg:grid-cols-[minmax(0,340px),minmax(0,1fr)]">
                {/* ---------------- Formulário ---------------- */}
                <div className="rounded-lg border border-slate-800 bg-slate-950/40 p-4 space-y-4">
                    <h2 className="text-base font-semibold">
                        {editingId ? "Editar modelo" : "Novo modelo"}
                    </h2>

                    <form className="space-y-4" onSubmit={handleSubmit}>
                        <div className="space-y-1">
                            <label htmlFor="code" className="text-sm font-medium">
                                Código
                            </label>
                            <Input
                                id="code"
                                value={form.code}
                                onChange={(e) => handleChange("code", e.target.value)}
                                placeholder="MANHA, TARDE, NOITE..."
                                required
                                disabled={saving}
                            />
                        </div>

                        <div className="space-y-1">
                            <label htmlFor="name" className="text-sm font-medium">
                                Nome
                            </label>
                            <Input
                                id="name"
                                value={form.name}
                                onChange={(e) => handleChange("name", e.target.value)}
                                placeholder="Turno da manhã (06h-14h)"
                                required
                                disabled={saving}
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1">
                                <label htmlFor="start" className="text-sm font-medium">
                                    Início
                                </label>
                                <Input
                                    id="start"
                                    type="time"
                                    value={form.start}
                                    onChange={(e) => handleChange("start", e.target.value)}
                                    disabled={saving}
                                />
                            </div>
                            <div className="space-y-1">
                                <label htmlFor="end" className="text-sm font-medium">
                                    Fim
                                </label>
                                <Input
                                    id="end"
                                    type="time"
                                    value={form.end}
                                    onChange={(e) => handleChange("end", e.target.value)}
                                    disabled={saving}
                                />
                            </div>
                        </div>

                        <div className="space-y-1">
                            <label
                                htmlFor="breakMinutes"
                                className="text-sm font-medium"
                            >
                                Pausa (min)
                            </label>
                            <Input
                                id="breakMinutes"
                                type="number"
                                min={0}
                                value={form.breakMinutes}
                                onChange={(e) =>
                                    handleChange("breakMinutes", e.target.value)
                                }
                                placeholder="0"
                                disabled={saving}
                            />
                        </div>

                        <div className="flex items-center justify-between gap-4">
                            <label className="flex items-center gap-2 text-sm">
                                <input
                                    id="isNight"
                                    type="checkbox"
                                    className="h-4 w-4 rounded border-slate-600 bg-slate-900"
                                    checked={form.isNight}
                                    onChange={(e) =>
                                        handleChange("isNight", e.target.checked)
                                    }
                                    disabled={saving}
                                />
                                Turno noturno
                            </label>

                            <label className="flex items-center gap-2 text-sm">
                                <input
                                    id="isActive"
                                    type="checkbox"
                                    className="h-4 w-4 rounded border-slate-600 bg-slate-900"
                                    checked={form.isActive}
                                    onChange={(e) =>
                                        handleChange("isActive", e.target.checked)
                                    }
                                    disabled={saving}
                                />
                                Ativo
                            </label>
                        </div>

                        <div className="flex gap-2 pt-2">
                            <Button type="submit" disabled={saving}>
                                {saving
                                    ? "A guardar..."
                                    : editingId
                                        ? "Guardar alterações"
                                        : "Criar modelo"}
                            </Button>
                            {editingId && (
                                <Button
                                    type="button"
                                    variant="outline"
                                    disabled={saving}
                                    onClick={resetForm}
                                >
                                    Cancelar edição
                                </Button>
                            )}
                        </div>
                    </form>
                </div>

                {/* ---------------- Tabela ---------------- */}
                <div className="rounded-lg border border-slate-800 bg-slate-950/40 p-4 space-y-2">
                    <h2 className="text-base font-semibold">Modelos cadastrados</h2>

                    {loading ? (
                        <div className="text-sm text-slate-300">
                            A carregar modelos de turno...
                        </div>
                    ) : items.length === 0 ? (
                        <div className="text-sm text-slate-300">
                            Ainda não existem modelos cadastrados.
                        </div>
                    ) : (
                        <div className="border border-slate-800 rounded-md overflow-hidden">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="w-[90px]">Código</TableHead>
                                        <TableHead>Nome</TableHead>
                                        <TableHead className="w-[110px]">Horário</TableHead>
                                        <TableHead className="w-[90px] text-center">
                                            Pausa
                                        </TableHead>
                                        <TableHead className="w-[90px] text-center">
                                            Noturno
                                        </TableHead>
                                        <TableHead className="w-[90px] text-center">
                                            Status
                                        </TableHead>
                                        <TableHead className="w-[130px]" />
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {items.map((item) => (
                                        <TableRow key={item.id}>
                                            <TableCell className="font-mono text-xs">
                                                {item.code}
                                            </TableCell>
                                            <TableCell>{item.name}</TableCell>
                                            <TableCell className="text-sm">
                                                {toTimeField(item.start_time) || "--:--"}{" "}
                                                {" - "}
                                                {toTimeField(item.end_time) || "--:--"}
                                            </TableCell>
                                            <TableCell className="text-center text-sm">
                                                {item.break_minutes ?? 0} min
                                            </TableCell>
                                            <TableCell className="text-center text-sm">
                                                {item.is_night_shift ? "Sim" : "Não"}
                                            </TableCell>
                                            <TableCell className="text-center text-sm">
                                                {item.is_active === false ? "Inativo" : "Ativo"}
                                            </TableCell>
                                            <TableCell className="text-right space-x-2">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => handleEdit(item)}
                                                >
                                                    Editar
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="border-red-500 text-red-300 hover:bg-red-500/10"
                                                    onClick={() => handleDelete(item.id)}
                                                >
                                                    Remover
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
